'use client';

import { InteractionRequiredAuthError, InteractionType } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal, useMsalAuthentication } from '@azure/msal-react';
import { Suspense, useEffect, useMemo } from 'react';

import { checkUserAuthorization } from './checkUserAuthorization';
import { hasValue } from './hasValue';

export interface AzureLoginProps {
  children: React.ReactNode;
  scopes: string[];
  authenticationInteractionType?: InteractionType;
  loginInteractionType?: InteractionType;
  skipAuthCheck?: boolean;
  loadingComponent?: React.ReactNode;
  allowedRoles?: string[] | null;
  handleUnauthorized?: () => void;
}

export function MsalLogin(props: Readonly<AzureLoginProps>) {
  const {
    scopes,
    authenticationInteractionType = InteractionType.Silent,
    loginInteractionType = InteractionType.Redirect,
    skipAuthCheck = false,
    children,
    loadingComponent,
    allowedRoles = null,
    handleUnauthorized,
  } = props;

  const {
    instance,
    inProgress,
    accounts,
  } = useMsal();

  const {
    login,
    error,
  } = useMsalAuthentication(authenticationInteractionType, {
    scopes,
    account: accounts[0],
  });

  const isInitialized = useMemo(() => inProgress === InteractionType.None, [inProgress]);
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    if (accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [accounts, instance]);

  useEffect(() => {
    const noAccountAvailable = accounts.length < 1;
    const hasAuthError = error instanceof InteractionRequiredAuthError;

    if ((hasAuthError || noAccountAvailable) && isInitialized && !skipAuthCheck) {
      login(loginInteractionType, {
        scopes,
      });
    }
  }, [error, accounts.length, isAuthenticated, isInitialized, skipAuthCheck, login, loginInteractionType, scopes]);

  useEffect(() => {
    if (!skipAuthCheck && isInitialized) {
      // Optional check for authorization
      const account = instance.getActiveAccount();

      if (hasValue(allowedRoles) && hasValue(handleUnauthorized)) {
        if (!checkUserAuthorization(account, allowedRoles)) {
          // Redirect to unauthorized page if user doesn't have any allowed role
          handleUnauthorized();
        }
      }
    }
  }, [allowedRoles, handleUnauthorized, instance, skipAuthCheck, isInitialized]);

  if (!isInitialized && !skipAuthCheck) {
    // Return the loader or  show nothing when no loader is provided
    return loadingComponent || null;
  }

  if (isInitialized && !isAuthenticated && !skipAuthCheck) {
    // Return the loader or  show nothing when no loader is provided
    return loadingComponent || null;
  }

  return (
    <Suspense fallback={loadingComponent || null}>
      {children}
    </Suspense>
  );
}

export default MsalLogin;
